// Order flow for the course (OrderFlowCharts.js, contentOrderFlow.js): footprint bars (the volume
// traded at each price, split into contracts sold at the bid and bought at the ask), their delta,
// point of control and imbalances, and DOM ladders (the resting orders around the price).
//
// A footprint bar is written as { open, close, low, high, base, bias, slope, at, seed }: `base` is
// the volume at the middle of the bar, `bias` leans every price toward buyers (+) or sellers (−),
// `slope` leans the top of the bar one way and the bottom the other, and `at` sets prices outright
// ({ price: [bid, ask] }: a climax, absorption, a zero print at the high…).

export const TICK = 0.25;
const round = (n) => Math.round(n * 1e6) / 1e6;

export function footprintBar({ open, close, low, high, base = 120, bias = 0, slope = 0, at = {}, seed = 1 }) {
  let s = (seed * 9301 + 49297) % 233280 || 1;
  const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const rows = [];
  const mid = (low + high) / 2;
  const half = Math.max((high - low) / 2, TICK);
  for (let p = high; p >= low - 1e-9; p = round(p - TICK)) {
    const price = round(p);
    if (at[price] !== undefined) { rows.push({ price, bid: at[price][0], ask: at[price][1] }); continue; }
    const shape = 1 - 0.75 * Math.abs(price - mid) / half;
    const total = Math.max(2, Math.round(base * shape * (0.75 + rand() * 0.5)));
    const share = Math.min(0.92, Math.max(0.08, 0.5 + bias + slope * ((price - mid) / half)));
    const ask = Math.round(total * share);
    rows.push({ price, bid: total - ask, ask });
  }
  const bid = rows.reduce((t, r) => t + r.bid, 0);
  const ask = rows.reduce((t, r) => t + r.ask, 0);
  const poc = rows.reduce((best, r) => (r.bid + r.ask > best.bid + best.ask ? r : best), rows[0]);
  return { open, close, low, high, rows, volume: bid + ask, delta: ask - bid, poc: poc.price };
}

// Diagonal imbalances, the way footprint charts compare them: buyers at the ask at a price against
// sellers at the bid one tick below (and the mirror for sellers). `ratio` 3 = 300%.
export function imbalances(bar, { ratio = 3, min = 20 } = {}) {
  const at = new Map(bar.rows.map((r) => [r.price, r]));
  const buy = [];
  const sell = [];
  for (const r of bar.rows) {
    const below = at.get(round(r.price - TICK));
    const above = at.get(round(r.price + TICK));
    if (below && r.ask >= min && r.ask >= ratio * Math.max(below.bid, 1)) buy.push(r.price);
    if (above && r.bid >= min && r.bid >= ratio * Math.max(above.ask, 1)) sell.push(r.price);
  }
  return { buy, sell };
}

// Runs of 3 or more imbalances in a row (stacked imbalances), as [low, high] price ranges.
export function stacked(prices, n = 3) {
  const sorted = [...prices].sort((a, b) => a - b);
  const out = [];
  let run = [];
  for (const p of sorted) {
    if (run.length && round(p - run[run.length - 1]) !== TICK) { if (run.length >= n) out.push([run[0], run[run.length - 1]]); run = []; }
    run.push(p);
  }
  if (run.length >= n) out.push([run[0], run[run.length - 1]]);
  return out;
}

// A DOM ladder around `last`: resting asks above and bids at and below it, with `sizes` for chosen
// prices ({ price: size }: a big wall…), the rest small and uneven. Rows high to low.
export function domLadder({ last, depth = 10, base = 40, sizes = {}, seed = 1 }) {
  let s = (seed * 7919 + 13) % 233280 || 1;
  const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const rows = [];
  // The best bid is at the last price and the best ask one tick above it.
  for (let k = depth; k > -depth; k -= 1) {
    const price = round(last + k * TICK);
    const size = sizes[price] !== undefined ? sizes[price] : Math.round(base * (0.5 + rand()) + Math.abs(k) * 3);
    rows.push(k > 0 ? { price, ask: size, bid: null } : { price, bid: size, ask: null, last: k === 0 });
  }
  return rows;
}
